import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import { Package, Search } from "lucide-react";

const STATUSES = ["", "in_stock", "issued", "used", "in_transit_recovery", "faulty", "repair", "scrap"];

export default function Serials() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ status: "", warehouse_id: "", product_id: "", q: "" });
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/warehouses").then(r => setWarehouses(r.data));
    api.get("/products").then(r => setProducts(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/stock/serials", { params }).then(r => setItems(r.data)).catch(e => setErr(formatApiError(e)));
  }, [filters]);

  return (
    <div className="p-4 md:p-8" data-testid="serials-page">
      <PageHeader title="Serial Numbers" subtitle="Every serialized unit in stock, issued, used, or recovered." />

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
            <input data-testid="serial-search" placeholder="Search serial number…" value={filters.q} onChange={e=>setFilters(f=>({...f, q:e.target.value}))} className="inp pl-9" />
          </div>
          <select value={filters.status} onChange={e=>setFilters(f=>({...f, status:e.target.value}))} className="inp" data-testid="serial-status-filter">
            {STATUSES.map(s => <option key={s} value={s}>{s ? s.replace(/_/g, " ").toUpperCase() : "All statuses"}</option>)}
          </select>
          <select value={filters.warehouse_id} onChange={e=>setFilters(f=>({...f, warehouse_id:e.target.value}))} className="inp" data-testid="serial-wh-filter">
            <option value="">All warehouses</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select value={filters.product_id} onChange={e=>setFilters(f=>({...f, product_id:e.target.value}))} className="inp md:col-span-4" data-testid="serial-product-filter">
            <option value="">All products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
          </select>
        </div>
      </Card>

      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {items.length === 0 ? <EmptyState title="No serials match" icon={Package} /> :
        <Card className="overflow-hidden">
          <div className="text-xs text-zinc-500 px-4 py-2 border-b border-zinc-100 num">{items.length} serial(s)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Serial #</th>
                  <th className="px-4 py-3 text-left">SKU</th>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Warehouse</th>
                  <th className="px-4 py-3 text-left">Issued To</th>
                  <th className="px-4 py-3 text-left">Project</th>
                  <th className="px-4 py-3 text-left">Condition</th>
                </tr>
              </thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3 num font-semibold">{s.serial_no}</td>
                    <td className="px-4 py-3 num text-xs">{s.sku}</td>
                    <td className="px-4 py-3">{s.product_name}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700">{(s.status || "").replace(/_/g, " ")}</span></td>
                    <td className="px-4 py-3 text-zinc-500">{s.warehouse_name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500">{s.issued_to_name || "—"}</td>
                    <td className="px-4 py-3 num text-xs">{s.project_label || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500">{s.condition || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}
